import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import "./Navbar.css";

const Navbar = ({ variant = "public" }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/");
    setIsMobileMenuOpen(false);
  };

  const isActive = (path) => {
    return location.pathname === path ? "active" : "";
  };

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/services", label: "Website" },
    { path: "/ecommerce", label: "E-Commerce" },
    { path: "/about", label: "About" },
    { path: "/contact", label: "Contact" },
  ];

  // Auth pages (Login/Register) - show minimal navigation
  const isAuthPage = [
    "/login",
    "/register",
    "/forgot-password",
    "/reset-password",
  ].includes(location.pathname);

  return (
    <nav
      className={`navbar ${isScrolled ? "scrolled" : ""} ${variant} ${
        isAuthPage ? "navbar-auth" : ""
      }`}
    >
      <div className="navbar-container">
        {/* Logo */}
        <Link to="/" className="navbar-logo">
          <img
            src="/images/logo/logo-mark.png"
            alt="Toora Flex"
            className="navbar-logo-img"
          />
          <span className="navbar-logo-text">
            Toora<span>flex</span>
          </span>
        </Link>

        {/* Auth Page Navigation - Simple back button */}
        {isAuthPage ? (
          <div className="navbar-auth-nav">
            <Link to="/" className="navbar-back-link">
              <i className="fas fa-arrow-left"></i> Back to Home
            </Link>
          </div>
        ) : (
          <>
            {/* Desktop Navigation */}
            <ul className="navbar-links">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className={isActive(link.path)}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Right side - Auth buttons or User dropdown */}
            <div className="navbar-actions">
              {user ? (
                <>
                  {user.Role === "admin" && (
                    <Link to="/admin" className="navbar-admin-btn">
                      <i className="fas fa-crown"></i> Admin
                    </Link>
                  )}
                  {user.Role !== "admin" && (
                    <Link to="/dashboard" className="navbar-dashboard-btn">
                      <i className="fas fa-tachometer-alt"></i> Dashboard
                    </Link>
                  )}
                  <button onClick={handleLogout} className="navbar-logout-btn">
                    <i className="fas fa-sign-out-alt"></i> Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="navbar-login-btn">
                    <svg
                      className="btn-icon"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                      <polyline points="10 17 15 12 10 7" />
                      <line x1="15" y1="12" x2="3" y2="12" />
                    </svg>
                    Sign In
                  </Link>
                  <Link to="/register" className="navbar-register-btn">
                    Get Started
                    <svg
                      className="btn-icon"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="navbar-mobile-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <i
                className={`fas ${isMobileMenuOpen ? "fa-times" : "fa-bars"}`}
              ></i>
            </button>
          </>
        )}
      </div>

      {/* Mobile Menu */}
      {!isAuthPage && (
        <div className={`navbar-mobile-menu ${isMobileMenuOpen ? "open" : ""}`}>
          <ul>
            {navLinks.map((link) => (
              <li key={link.path}>
                <Link
                  to={link.path}
                  className={isActive(link.path)}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              </li>
            ))}
            <li className="navbar-mobile-divider"></li>
            {user ? (
              <>
                {user.Role !== "admin" && (
                  <li>
                    <Link
                      to="/dashboard"
                      className="navbar-mobile-dashboard"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <i className="fas fa-tachometer-alt"></i> Dashboard
                    </Link>
                  </li>
                )}
                {user.Role === "admin" && (
                  <li>
                    <Link
                      to="/admin"
                      className="navbar-mobile-admin"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <i className="fas fa-crown"></i> Admin
                    </Link>
                  </li>
                )}
                <li>
                  <button
                    onClick={handleLogout}
                    className="navbar-mobile-logout"
                  >
                    <i className="fas fa-sign-out-alt"></i> Logout
                  </button>
                </li>
              </>
            ) : (
              <>
                <li>
                  <Link
                    to="/login"
                    className="navbar-mobile-login"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="fas fa-sign-in-alt"></i> Sign In
                  </Link>
                </li>
                <li>
                  <Link
                    to="/register"
                    className="navbar-mobile-register"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Get Started <i className="fas fa-arrow-right"></i>
                  </Link>
                </li>
              </>
            )}
          </ul>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
